import { Link } from '@tanstack/react-router';
import {
  Lightbulb, Info, AlertTriangle, ExternalLink, Mail, BookOpen,
  Sparkles, LayoutDashboard, Wallet, Workflow, Users, Shield,
  Database, HelpCircle, Keyboard,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle, CardDescription } from '@/components/Card';
import { Kbd } from '@/components/Kbd';
import { Button } from '@/components/Button';
import { useUI } from '@/store/ui';

/**
 * /user-guide — long-form product manual rendered with native app styling.
 * Sections are anchored so the TOC links jump in-page. The content mirrors
 * the published TAN Digital user guide; runtime affordances (open the
 * copilot, jump to a module) are wired to real handlers/routes where
 * relevant so the guide doubles as navigation.
 */
export function UserGuide() {
  const openCopilot = useUI((s) => s.openCopilot);
  return (
    <>
      <PageHeader
        crumbs={[{ label: 'User Guide' }]}
        title="Logica ERP — User Guide"
        subtitle="A connected suite where every product understands you — ask in plain words, get meaningful answers."
      />
      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-12 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,220px] gap-8">
          {/* MAIN COLUMN */}
          <article className="space-y-10 min-w-0">
            <Intro />

            <Section id="getting-started" title="1. Getting Started" icon={Sparkles}>
              <p>
                Welcome to Logica ERP — one home for your operations, finance, and resources,
                shaped around how you actually run your business rather than how an ERP
                assumes you do.
              </p>
              <SubHead>What Logica ERP is</SubHead>
              <p>
                Logica ERP is your single source of truth. Every order, invoice, journal entry,
                and resource lives in one connected data model, so the number you see in
                finance is the same number operations sees. There is no reconciling between
                systems, because there is only one system.
              </p>
              <p>
                An AI copilot lives inside the platform. It drafts entries, reconciles
                accounts, plans work, and answers questions about your business in plain
                language — but it never acts on its own. Nothing posts, ships, or changes
                without your approval.
              </p>
              <SubHead>Logging in</SubHead>
              <Ol>
                <li>Open your Logica workspace URL (for example, <Code>your-company.logica.id</Code>).</li>
                <li>Enter your email and password, or sign in with your organization's single sign-on (SSO).</li>
                <li>If multi-factor authentication is enabled, confirm the prompt on your device.</li>
                <li>You'll land on your home workspace.</li>
              </Ol>
              <Tip>
                Forgot your password? Use <strong>Reset password</strong> on the login screen. If your
                account uses SSO, you'll be redirected to your company's identity provider instead.
              </Tip>
              <SubHead>Navigating the workspace</SubHead>
              <p>The interface has three consistent zones:</p>
              <Ul>
                <li><strong>Left sidebar</strong> — your modules and saved views. Collapse it for more room.</li>
                <li><strong>Top bar</strong> — global search, the copilot button, notifications, and your profile.</li>
                <li><strong>Main canvas</strong> — wherever your work happens: a dashboard, a ledger, a list, or a map.</li>
              </Ul>
              <Note>Press <Kbd>/</Kbd> anywhere to jump straight to search. It's the fastest way to move around.</Note>
              <SubHead>Meeting the copilot</SubHead>
              <p>
                Click the copilot button in the top bar (or press <Kbd>C</Kbd>) to open it from any
                screen. Ask it anything in plain words:
              </p>
              <Quote items={[
                'Show me unpaid invoices over 30 days.',
                'Draft the month-end accruals.',
                'What changed in inventory this week?',
              ]} />
              <p>
                The copilot reads from your live data and responds with answers, drafts, or
                actions for you to approve.
              </p>
              <Button variant="secondary" className="mt-2" onClick={openCopilot}>
                <Sparkles className="size-4" /> Open the copilot
              </Button>
            </Section>

            <Section id="dashboard" title="2. Dashboard & Workspaces" icon={LayoutDashboard}>
              <SubHead>The home view</SubHead>
              <p>
                Your dashboard is the first thing you see — a live summary of what matters to
                your role. Owners see cash and revenue; operations leads see open orders and
                fulfillment; finance sees close status and reconciliations.
              </p>
              <p>
                Every card is live. The figures update as your business does, drawn from the
                same connected data model the whole suite reads from.
              </p>
              <SubHead>Customizing your dashboard</SubHead>
              <Ol>
                <li>Click <strong>Edit layout</strong> in the top-right of the dashboard.</li>
                <li>Drag cards to reorder them, or resize by dragging a corner.</li>
                <li>Click <strong>Add card</strong> to pull in a new metric, list, or chart.</li>
                <li>Click <strong>Save</strong> to lock your layout.</li>
              </Ol>
              <Tip>
                Ask the copilot <em>"Add a card showing this month's gross margin"</em> and it will
                build it for you.
              </Tip>
              <SubHead>Switching contexts</SubHead>
              <p>
                If you manage more than one entity, branch, or project, use the{' '}
                <strong>workspace switcher</strong> at the top of the sidebar. Each workspace
                keeps its own data, dashboards, and permissions, but the experience stays
                identical, so moving between them is seamless.
              </p>
            </Section>

            <Section id="finance" title="3. Finance" icon={Wallet}>
              <p>
                Finance is the heart of Logica ERP. The chart of accounts, ledgers, and reports
                all read from the same connected source — and the copilot works inside the
                ledger alongside you.
              </p>
              <SubHead>Chart of accounts</SubHead>
              <p>Your chart of accounts is the backbone of your books.</p>
              <Ul>
                <li>View it under <RouteLink to="/accounting/accounts">Finance → Chart of Accounts</RouteLink>.</li>
                <li>Accounts are grouped by type: assets, liabilities, equity, income, and expense.</li>
                <li>Add an account with <strong>New account</strong>, set its type and parent, and save.</li>
              </Ul>
              <Warn>
                Changing an account's type after it has transactions can affect historical
                reports. Logica will warn you before allowing it.
              </Warn>
              <SubHead>Journal entries</SubHead>
              <Ol>
                <li>Go to <RouteLink to="/accounting/journal-entries">Finance → Journal Entries</RouteLink>.</li>
                <li>Click <strong>New entry</strong>.</li>
                <li>Add lines — each with an account, a debit or credit, and an optional description.</li>
                <li>Logica checks that debits equal credits before you can post.</li>
                <li>Click <strong>Post</strong>, or <strong>Save as draft</strong> to finish later.</li>
              </Ol>
              <Tip>
                Instead of building an entry by hand, tell the copilot{' '}
                <em>"Record a IDR 12,000,000 rent payment for June from the BCA account"</em> —
                it drafts the balanced entry for your review.
              </Tip>
              <SubHead>The copilot in the ledger</SubHead>
              <p>The copilot can:</p>
              <Ul>
                <li><strong>Draft entries</strong> from a plain-language description.</li>
                <li><strong>Reconcile accounts</strong> by matching transactions to statements and flagging what doesn't fit.</li>
                <li><strong>Answer questions</strong> about your numbers — <em>"Why did expenses jump in May?"</em> — reading directly from your live ledger.</li>
              </Ul>
              <p>
                Everything the copilot produces is a draft. You review, adjust, and approve
                before anything posts.
              </p>
              <SubHead>Reports</SubHead>
              <p>
                Under <RouteLink to="/accounting/reports">Finance → Reports</RouteLink> you'll
                find the profit &amp; loss, balance sheet, cash flow, trial balance, and tax
                summaries (PPN, PPh, e-Faktur). Every report is live and drillable — click
                any figure to see the transactions behind it.
              </p>
            </Section>

            <Section id="operations" title="4. Operations" icon={Workflow}>
              <p>Operations covers how work flows through your business: orders in, goods and services out.</p>
              <SubHead>Orders</SubHead>
              <Ul>
                <li><strong>Sales orders</strong> capture what customers buy. Create one under <RouteLink to="/accounting/sales-orders">Operations → Sales Orders</RouteLink>, add line items, and confirm.</li>
                <li><strong>Purchase orders</strong> capture what you buy from suppliers. Build them under <RouteLink to="/accounting/purchase-orders">Operations → Purchase Orders</RouteLink>.</li>
                <li>Confirmed orders flow straight into finance and inventory — no re-entry.</li>
              </Ul>
              <SubHead>Inventory</SubHead>
              <p>
                Track stock across locations under{' '}
                <RouteLink to="/stock">Operations → Inventory</RouteLink>. Each item shows quantity
                on hand, committed, and incoming. Stock moves automatically as orders are
                fulfilled and received.
              </p>
              <Note>
                Set reorder points per item, and Logica will flag when stock runs low — or ask
                the copilot to <em>"list everything below its reorder point."</em>
              </Note>
              <SubHead>Procurement</SubHead>
              <p>
                The procurement flow connects requests, approvals, purchase orders, and
                supplier bills. Raise a request, route it for approval, convert it to a PO,
                and match the supplier bill on arrival — all in one trail.
              </p>
              <SubHead>Fulfillment</SubHead>
              <p>
                As orders ship, mark them fulfilled to update inventory and trigger invoicing.
                The copilot can plan and prioritize fulfillment if you ask it to.
              </p>
            </Section>

            <Section id="resources" title="5. Resources" icon={Users}>
              <p>Resources is where your people, assets, and schedules live.</p>
              <SubHead>People</SubHead>
              <p>
                Manage your team under{' '}
                <RouteLink to="/hr">Resources → People</RouteLink> — roles, contact details, and
                access. People records connect to payroll, approvals, and BPJS compliance
                where relevant.
              </p>
              <SubHead>Assets</SubHead>
              <p>
                Track equipment, vehicles, and other assets under{' '}
                <RouteLink to="/assets">Resources → Assets</RouteLink>. Each asset carries its
                value, depreciation schedule, and location, and posts depreciation to finance
                automatically.
              </p>
              <SubHead>Scheduling</SubHead>
              <p>
                Plan who does what, and when, under{' '}
                <RouteLink to="/projects">Resources → Scheduling</RouteLink>. Assign work, see
                capacity at a glance, and let the copilot suggest a schedule based on workload.
              </p>
            </Section>

            <Section id="copilot" title="6. The AI Copilot" icon={Sparkles}>
              <p>
                The copilot is the same intelligence everywhere in Logica — open it from any
                screen with the top-bar button or <Kbd>C</Kbd>.
              </p>
              <SubHead>What it can do</SubHead>
              <Ul>
                <li><strong>Answer questions</strong> about your business in plain language.</li>
                <li><strong>Draft</strong> journal entries, orders, schedules, and more.</li>
                <li><strong>Reconcile</strong> accounts and surface anomalies.</li>
                <li><strong>Explain</strong> any number by showing the transactions behind it.</li>
                <li><strong>Build</strong> dashboard cards and reports on request.</li>
              </Ul>
              <SubHead>Asking good questions</SubHead>
              <p>
                The copilot understands plain words — you don't need special syntax. The
                clearer your question, the sharper the answer:
              </p>
              <Quote items={[
                'Show me top 10 customers by revenue this quarter.',
                'Draft a credit note for invoice INV-2041.',
                'Compare this month’s expenses to last month, by category.',
              ]} />
              <SubHead>Guardrails and approvals</SubHead>
              <p>
                This is the rule that never bends: <strong>the copilot drafts and suggests,
                but you decide.</strong> Nothing posts, ships, sends, or changes without
                explicit approval.
              </p>
              <Ul>
                <li>Drafts appear clearly marked as drafts.</li>
                <li>You can edit anything before approving.</li>
                <li>Every copilot action is logged for audit.</li>
              </Ul>
              <Note>
                Administrators can tune what the copilot is allowed to draft per role under{' '}
                <RouteLink to="/settings/appearance">Settings → Copilot</RouteLink>.
              </Note>
            </Section>

            <Section id="data-model" title="7. Data Model — Your Single Source of Truth" icon={Database}>
              <p>
                Everything in Logica reads from one connected data model. This is what makes
                the suite feel like one product rather than many.
              </p>
              <Ul>
                <li>A customer exists <strong>once</strong> — referenced by sales, finance, and reporting alike.</li>
                <li>A posted transaction is <strong>immediately</strong> visible everywhere it's relevant.</li>
                <li>There is <strong>no syncing</strong> between modules, because there is nothing to sync.</li>
              </Ul>
              <p>
                This is why the geospatial, governance, and executive-insight layers on top of
                Logica ERP can all read the same operational truth — and why the answer the
                copilot gives finance is the same answer it gives operations.
              </p>
            </Section>

            <Section id="admin" title="8. Admin & Settings" icon={Shield}>
              <SubHead>Users and roles</SubHead>
              <p>
                Under <RouteLink to="/settings/appearance">Settings → Users</RouteLink>, invite
                teammates by email and assign a role. Roles bundle permissions, so you grant
                access by job rather than by individual screen.
              </p>
              <SubHead>Permissions</SubHead>
              <p>
                Fine-tune what each role can see and do under{' '}
                <RouteLink to="/settings/appearance">Settings → Roles</RouteLink>. Permissions
                are granular — view, create, edit, approve, post — and apply per module.
              </p>
              <Warn>
                The <strong>Owner</strong> role has full access, including billing and the
                ability to delete the workspace. Assign it sparingly.
              </Warn>
              <SubHead>Integrations</SubHead>
              <p>
                Connect Logica to your bank, tax systems (e-Faktur), and other tools under{' '}
                <RouteLink to="/settings/appearance">Settings → Integrations</RouteLink>. Each
                integration shows its connection status and last sync.
              </p>
              <SubHead>Workspace settings</SubHead>
              <p>
                Set your company details, currency, fiscal year, and tax configuration under{' '}
                <RouteLink to="/settings/appearance">Settings → Workspace</RouteLink>. These shape
                how the whole platform behaves.
              </p>
            </Section>

            <Section id="faq" title="9. Troubleshooting & FAQ" icon={HelpCircle}>
              <Faq q="My dashboard numbers look wrong.">
                Every figure is drillable — click it to see the transactions behind it. If a
                number still looks off, ask the copilot <em>"explain this figure"</em> and it
                will trace the source.
              </Faq>
              <Faq q="A journal entry won't post.">
                Logica requires debits to equal credits. Check the entry total; the imbalance
                is shown at the bottom of the entry.
              </Faq>
              <Faq q="I can't see a module in my sidebar.">
                Module visibility follows your role. Ask an administrator to adjust your
                permissions under <RouteLink to="/settings/appearance">Settings → Roles</RouteLink>.
              </Faq>
              <Faq q="The copilot posted something I didn't want.">
                It can't — the copilot only ever drafts. If something posted, a person
                approved it. Check the audit log under{' '}
                <RouteLink to="/settings/appearance">Settings → Activity</RouteLink> to see who.
              </Faq>
              <Faq q="How do I export a report?">
                Open any report and click <strong>Export</strong> for PDF or spreadsheet.
                Reports stay live until the moment you export.
              </Faq>
              <Faq q="Is my data secure and in-country?">
                Logica runs on Aksara Managed Cloud Service — sovereign, in-country
                infrastructure that TAN operates and secures.
              </Faq>
            </Section>

            <Section id="shortcuts" title="10. Keyboard Shortcuts & Glossary" icon={Keyboard}>
              <SubHead>Keyboard shortcuts</SubHead>
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-body-sm border-collapse">
                  <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                    <tr>
                      <th className="text-left font-medium px-3 py-2 w-[200px]">Shortcut</th>
                      <th className="text-left font-medium px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <KbdRow keys={['/']}             label="Focus global search" />
                    <KbdRow keys={['C']}             label="Open the copilot" />
                    <KbdRow keys={['G', 'D']}        label="Go to Dashboard" />
                    <KbdRow keys={['G', 'F']}        label="Go to Finance" />
                    <KbdRow keys={['G', 'O']}        label="Go to Operations" />
                    <KbdRow keys={['N']}             label="New record (context-aware)" />
                    <KbdRow keys={['Esc']}           label="Close panel / clear search" />
                  </tbody>
                </table>
              </div>
              <SubHead>Glossary</SubHead>
              <dl className="space-y-2.5 text-body-sm">
                <GlossaryItem term="Chart of accounts" body="The structured list of every account your books use." />
                <GlossaryItem term="Copilot" body="The AI assistant built into Logica that drafts, reconciles, and answers in plain language." />
                <GlossaryItem term="Data model" body="The single connected structure all modules read from." />
                <GlossaryItem term="e-Faktur" body="Indonesia's electronic tax invoice system." />
                <GlossaryItem term="Journal entry" body="A balanced record of debits and credits posted to the ledger." />
                <GlossaryItem term="PPN / PPh" body="Indonesian value-added tax and income tax." />
                <GlossaryItem term="Reconciliation" body="Matching your records against an external source, like a bank statement." />
                <GlossaryItem term="Workspace" body="A self-contained entity, branch, or project with its own data and permissions." />
              </dl>
            </Section>

            <Footer />
          </article>

          {/* TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-6 space-y-1 text-body-sm">
              <div className="text-micro-uppercase text-stone mb-2">On this page</div>
              <TocLink href="#getting-started">1. Getting Started</TocLink>
              <TocLink href="#dashboard">2. Dashboard &amp; Workspaces</TocLink>
              <TocLink href="#finance">3. Finance</TocLink>
              <TocLink href="#operations">4. Operations</TocLink>
              <TocLink href="#resources">5. Resources</TocLink>
              <TocLink href="#copilot">6. The AI Copilot</TocLink>
              <TocLink href="#data-model">7. Data Model</TocLink>
              <TocLink href="#admin">8. Admin &amp; Settings</TocLink>
              <TocLink href="#faq">9. Troubleshooting &amp; FAQ</TocLink>
              <TocLink href="#shortcuts">10. Shortcuts &amp; Glossary</TocLink>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

/* ---------------- helpers ---------------- */

function Intro() {
  return (
    <Card className="!p-5 bg-surface-soft border-hairline">
      <div className="flex items-start gap-3">
        <div className="size-10 rounded-md bg-brand-green text-on-primary inline-flex items-center justify-center shrink-0">
          <BookOpen className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <CardTitle>Version 1.0 · by TAN Digital</CardTitle>
          <CardDescription>
            One home for your operations, finance, and resources. The whole guide lives on
            this page — use the table of contents on the right to jump around.
          </CardDescription>
          <div className="mt-3 flex items-center gap-3 text-caption text-stone">
            <Mail className="size-3.5" />
            <a href="mailto:hello@tandigital.id" className="text-brand-green-ink hover:underline">
              hello@tandigital.id
            </a>
          </div>
        </div>
      </div>
    </Card>
  );
}

function Section({
  id, title, icon: Icon, children,
}: {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="flex items-center gap-2 mb-4">
        <div className="size-8 rounded-md bg-accent-soft text-accent-hover inline-flex items-center justify-center">
          <Icon className="size-4" />
        </div>
        <h2 className="text-section-head font-semibold text-ink">{title}</h2>
      </div>
      <div className="space-y-3 text-body text-charcoal leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function SubHead({ children }: { children: React.ReactNode }) {
  return <h3 className="text-body font-semibold text-ink mt-5 mb-1">{children}</h3>;
}

function Ol({ children }: { children: React.ReactNode }) {
  return <ol className="list-decimal pl-5 space-y-1.5">{children}</ol>;
}

function Ul({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 space-y-1.5">{children}</ul>;
}

function Code({ children }: { children: React.ReactNode }) {
  return <code className="font-mono text-caption bg-surface px-1.5 py-0.5 rounded">{children}</code>;
}

function Quote({ items }: { items: string[] }) {
  return (
    <ul className="border-l-2 border-brand-green pl-4 space-y-1.5 text-stone italic">
      {items.map((s, i) => <li key={i}>"{s}"</li>)}
    </ul>
  );
}

function Callout({
  tone, icon: Icon, children,
}: {
  tone: 'tip' | 'note' | 'warn';
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  const styles = {
    tip:  'bg-accent-soft/40 border-accent/30 text-charcoal',
    note: 'bg-surface-soft border-hairline text-charcoal',
    warn: 'bg-brand-error/5 border-brand-error/30 text-charcoal',
  }[tone];
  const iconColor = {
    tip:  'text-accent',
    note: 'text-stone',
    warn: 'text-brand-error',
  }[tone];
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-body-sm ${styles}`}>
      <Icon className={`size-4 shrink-0 mt-0.5 ${iconColor}`} />
      <div className="flex-1">{children}</div>
    </div>
  );
}
function Tip({ children }: { children: React.ReactNode })  { return <Callout tone="tip"  icon={Lightbulb}>{children}</Callout>; }
function Note({ children }: { children: React.ReactNode }) { return <Callout tone="note" icon={Info}>{children}</Callout>; }
function Warn({ children }: { children: React.ReactNode }) { return <Callout tone="warn" icon={AlertTriangle}>{children}</Callout>; }

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="rounded-md border border-hairline bg-canvas p-3 group open:bg-surface-soft">
      <summary className="cursor-pointer font-medium text-ink list-none flex items-center justify-between gap-2">
        <span>{q}</span>
        <span className="text-stone text-caption group-open:rotate-90 transition-transform">›</span>
      </summary>
      <div className="mt-2 text-body-sm text-charcoal leading-relaxed">{children}</div>
    </details>
  );
}

function KbdRow({ keys, label }: { keys: string[]; label: string }) {
  return (
    <tr className="border-t border-hairline">
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1">
          {keys.map((k, i) => (
            <span key={i} className="inline-flex items-center gap-1">
              <Kbd>{k}</Kbd>
              {i < keys.length - 1 && <span className="text-stone text-caption">then</span>}
            </span>
          ))}
        </span>
      </td>
      <td className="px-3 py-2 text-charcoal">{label}</td>
    </tr>
  );
}

function GlossaryItem({ term, body }: { term: string; body: string }) {
  return (
    <div className="flex gap-2">
      <dt className="font-medium text-ink shrink-0 min-w-[140px]">{term}</dt>
      <dd className="text-charcoal">— {body}</dd>
    </div>
  );
}

function RouteLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to as never} className="text-brand-green-ink hover:underline">
      {children}
    </Link>
  );
}

function TocLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className="block px-2 py-1 rounded text-stone hover:text-ink hover:bg-surface-soft transition-colors">
      {children}
    </a>
  );
}

function Footer() {
  return (
    <div className="pt-6 border-t border-hairline text-caption text-stone flex items-center justify-between flex-wrap gap-2">
      <span>Logica ERP · by TAN Digital</span>
      <a href="mailto:hello@tandigital.id" className="text-brand-green-ink hover:underline inline-flex items-center gap-1">
        <Mail className="size-3" /> hello@tandigital.id <ExternalLink className="size-3" />
      </a>
    </div>
  );
}
